// VoltageScale.jsx

import React, { useState, useEffect, useCallback } from 'react'
import { FormattedMessage, injectIntl } from 'react-intl'
import PropTypes from 'prop-types'
import updateRA from 'immutability-helper'
import { round } from 'altrac-conversions'
import {
  DecimalInput, isNumber,
} from '../Inputs/Inputs'

import AppDispatcher from '../../../dispatchers/AppDispatcher'

import styles from '../../../../css/settings.module.css'
import RedX from '../Graphics/RedX'

const DEFAULT_PHYSICAL = {
  inSigMin: 0.17995321,
  inSigMax: 0.89976606,
  inMin: 0,
  inMax: 100,
}

const V_SCALE = 5.557

const VoltageScale = (props) => {
  const {
    device: {
      physical = DEFAULT_PHYSICAL,
    } = {},
    setValidity,
  } = props

  const {
    inSigMin = DEFAULT_PHYSICAL.inSigMin,
    inSigMax = DEFAULT_PHYSICAL.inSigMax,
    inMin = DEFAULT_PHYSICAL.inMin,
    inMax = DEFAULT_PHYSICAL.inMax,
  } = physical

  const [formIsValid, setFormIsValid] = useState(true)
  const [invalidFields, setInvalidFields] = useState({})
  const [inSigMinHolder, setInSigMinHolder] = useState(inSigMin * V_SCALE)
  const [inSigMaxHolder, setInSigMaxHolder] = useState(inSigMax * V_SCALE)
  const [inMinHolder, setInMinHolder] = useState(inMin)
  const [inMaxHolder, setInMaxHolder] = useState(inMax)

  const appDispatch = useCallback(() => {
    const physicals = {
      inSigMin: inSigMinHolder / V_SCALE,
      inSigMax: inSigMaxHolder / V_SCALE,
      inMin: Number(inMinHolder),
      inMax: Number(inMaxHolder),
    }

    AppDispatcher.dispatch({
      action: 'change-physical',
      setting: physicals,
    })
  }, [inSigMinHolder, inSigMaxHolder, inMinHolder, inMaxHolder])

  useEffect(() => {
    appDispatch()
  }, [appDispatch])

  const childChange = useCallback((event) => {
    const { target: { name, value, validity: { valid } } } = event
    let newValue = value

    switch (name) {
      case 'inSigMinHolder':
        newValue = round(value, 3)
        setInSigMinHolder(newValue)
        break
      case 'inSigMaxHolder':
        newValue = round(value, 3)
        setInSigMaxHolder(newValue)
        break
      case 'inMinHolder':
        newValue = round(value, 3)
        setInMinHolder(newValue)
        break
      case 'inMaxHolder':
        newValue = round(value, 3)
        setInMaxHolder(newValue)
        break
      default:
        return
    }

    const newInvalidFields = { ...invalidFields }

    if (valid) {
      delete newInvalidFields[name]
    } else {
      newInvalidFields[name] = true
    }

    const newFormIsValid = Object.keys(newInvalidFields).length === 0

    setInvalidFields(newInvalidFields)
    setFormIsValid(newFormIsValid)
    setValidity(newFormIsValid)
  }, [invalidFields, setValidity])

  const unitStyle = { opacity: 0.7, marginBottom: '10px' }
  const redX = (
    <RedX
      style={{
        position: 'absolute',
        height: '20px',
        right: '4px',
        top: '8px',
        width: '20px',
      }}
    />
  )

  const textInputStyle = {
    height: '32px',
    position: 'relative',
    width: '100%',
  }

  const valueInRange = (
    value,
    min = 0,
    max = Number.MAX_VALUE
  ) => isNumber(value) && ((value < min) || (value > max)) && redX

  return (
    <div>
      <section className={styles.heading}>
        <h3>
          <FormattedMessage
            id='setting.device.voltageScale.label'
            defaultMessage='Voltage Calibration'
            description='Voltage Calibration label'
          />
        </h3>
      </section>
      <div className={styles.inputArea}>
        <section>
          <h5>
            <FormattedMessage
              id='setting.device.voltageScale.inSigMin'
              defaultMessage='In Signal Min'
              description='In Signal Min label'
            />
          </h5>
          <div className={styles.controlNumber}>
            <div style={textInputStyle}>
              <DecimalInput
                value={Math.round(inSigMinHolder * 10) / 10}
                childChange={childChange}
                field='inSigMinHolder'
                min={0}
                style={styles.controlNumberInput}
              />
            </div>
          </div>
        </section>
        <section>
          <h5>
            <FormattedMessage
              id='setting.device.voltageScale.inSigMax'
              defaultMessage='In Signal Max'
              description='In Signal Max label'
            />
          </h5>
          <div className={styles.controlNumber}>
            <div style={textInputStyle}>
              <DecimalInput
                value={Math.round(inSigMaxHolder * 10) / 10}
                childChange={childChange}
                field='inSigMaxHolder'
                min={0}
                style={styles.controlNumberInput}
              />
            </div>
          </div>
        </section>
        <section>
          <h5>
            <FormattedMessage
              id='setting.device.voltageScale.sigMin'
              defaultMessage='Value Min'
              description='Value Min label'
            />
          </h5>
          <div className={styles.controlNumber}>
            <div style={textInputStyle}>
              <DecimalInput
                value={Math.round(inMinHolder * 10) / 10}
                childChange={childChange}
                field='inMinHolder'
                min={0}
                style={styles.controlNumberInput}
              />
            </div>
          </div>
        </section>
        <section>
          <h5>
            <FormattedMessage
              id='setting.device.voltageScale.sigMax'
              defaultMessage='Value Max'
              description='Value Max label'
            />
          </h5>
          <div className={styles.controlNumber}>
            <div style={textInputStyle}>
              <DecimalInput
                value={Math.round(inMaxHolder * 10) / 10}
                childChange={childChange}
                field='inMaxHolder'
                min={0}
                style={styles.controlNumberInput}
              />
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}

VoltageScale.propTypes = {
  device: PropTypes.object.isRequired,
  setValidity: PropTypes.func.isRequired,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }),
}

export default injectIntl(VoltageScale)
